import React, { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FlowDialog } from 'components/flowDialog/FlowDialog';
import { CreateOrganizationStep } from 'features/organizations/createOrganizationFlow/CreateOrganizationStep';
import { AddUserToOrganizationStep } from 'features/organizations/createOrganizationFlow/AddUserToOrganizationStep';
import { AddContractInfoStep } from 'features/organizations/createOrganizationFlow/AddContractInfoStep';
import { AddMeasurementCardStep } from 'features/organizations/createOrganizationFlow/AddMeasurementCardStep';
import { OrganizationsTable } from 'features/organizations/OrganizationsTable';
import { PageToolbar } from 'components/PageToolbar';
import { ApplicationState } from 'store';
import PageSpinner from 'components/spinners/PageSpinner';
import { organizationActions } from 'actions/organizationActions';
import { OrganizationContractStatusFilter } from 'store/organizations';

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
  },
  flexGrow: {
    flexGrow: 1,
  },
  buttonClass: {
    marginLeft: 0,
    marginRight: 0,
  },
  headingButton: {
    textAlign: 'right',
  },

  plusIcon: {
    display: 'inline-block',
    fontSize: '1.5rem',
    lineHeight: 0,
    marginRight: '0.5rem',
  },
  filterContainer: {
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    padding: '0 1rem',
  },
  filterInput: {
    minWidth: '12rem',
  },
}));

const defaultPageSize = 25;

const parseActiveFilter = (value: string | null): boolean | undefined => {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
};

const parseContractStatusFilter = (
  value: string | null,
): OrganizationContractStatusFilter | undefined => {
  if (value === 'Expired' || value === 'ExpiringSoon' || value === 'Active') {
    return value;
  }

  return undefined;
};

const Organizations: React.FC = () => {
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [nameFilter, setNameFilter] = useState('');

  const organizationName = useSelector(
    (state: ApplicationState) => state.organization?.name,
  );

  const organizationId = useSelector(
    (state: ApplicationState) => state.organization?.id,
  );

  const userAccess = useSelector(
    (state: ApplicationState) => state.isLoggedIn?.userAccess,
  );

  const organizations = useSelector(
    (state: ApplicationState) => state.organizations?.organizations,
  );

  const query = new URLSearchParams(location.search);
  const page = Math.max(1, Number(query.get('page') ?? '1') || 1);
  const pageSize = Math.max(
    1,
    Number(query.get('pageSize') ?? defaultPageSize.toString()) ||
      defaultPageSize,
  );
  const activeFilter = parseActiveFilter(query.get('active'));
  const contractStatusFilter = parseContractStatusFilter(
    query.get('contractStatus'),
  );
  const queryNameFilter = query.get('name') ?? '';

  const handleOpenDialog = (): void => {
    dispatch({ type: 'SET_STEP', step: 1 });
    setIsOpen(true);
  };

  const handleCloseDialog = (): void => {
    setIsOpen(false);
  };

  useEffect(() => {
    setNameFilter(queryNameFilter);
  }, [queryNameFilter]);

  useEffect(() => {
    dispatch(
      organizationActions.getOrganizations({
        page,
        pageSize,
        name: queryNameFilter || undefined,
        active: activeFilter,
        contractStatus: contractStatusFilter,
      }),
    );
  }, [
    dispatch,
    page,
    pageSize,
    queryNameFilter,
    activeFilter,
    contractStatusFilter,
  ]);

  const updateQuery = (key: string, value?: string, resetPage = true) => {
    const next = new URLSearchParams(location.search);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }

    if (resetPage) {
      next.delete('page');
    }

    history.push({ search: next.toString() });
  };

  const handleNameFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateQuery('name', nameFilter.trim() || undefined);
  };

  const handleActiveFilterChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const value = event.target.value as string;
    updateQuery('active', value || undefined);
  };

  const handleContractStatusChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const value = event.target.value as string;
    updateQuery('contractStatus', value || undefined);
  };

  const handleResetFilters = () => {
    const next = new URLSearchParams(location.search);
    next.delete('name');
    next.delete('active');
    next.delete('contractStatus');
    next.delete('page');
    history.push({ search: next.toString() });
  };

  return (
    <>
      <PageToolbar>
        <div className={classes.toolbar}>
          <Typography variant="subtitle1">
            {t('organizations_page.title')}
          </Typography>
          <div className={classes.flexGrow}></div>
          <div className={classes.headingButton}>
            {userAccess?.isGlobalAdmin && (
              <Button
                variant="contained"
                color="primary"
                className={classes.buttonClass}
                onClick={handleOpenDialog}
              >
                <span className={classes.plusIcon}>+</span>
                {t('organizations_page.create_organization_btn')}
              </Button>
            )}
          </div>
        </div>
        <Divider />
        <div className={classes.filterContainer}>
          <div className={classes.filterRow}>
            <form onSubmit={handleNameFilterSubmit}>
              <TextField
                className={classes.filterInput}
                label={t('organizations_page.filters.search')}
                variant="outlined"
                size="small"
                value={nameFilter}
                onChange={(event) => setNameFilter(event.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton type="submit" size="small">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </form>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterInput}
            >
              <InputLabel id="organization-active-filter-label">
                {t('organizations_page.filters.status')}
              </InputLabel>
              <Select
                labelId="organization-active-filter-label"
                value={
                  activeFilter === undefined
                    ? ''
                    : activeFilter
                    ? 'true'
                    : 'false'
                }
                onChange={handleActiveFilterChange}
                label={t('organizations_page.filters.status')}
              >
                <MenuItem value="">
                  {t('organizations_page.filters.all_statuses')}
                </MenuItem>
                <MenuItem value="true">
                  {t('organizations_page.table.active')}
                </MenuItem>
                <MenuItem value="false">
                  {t('organizations_page.table.inactive')}
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterInput}
            >
              <InputLabel id="organization-contract-filter-label">
                {t('organizations_page.filters.contract_status')}
              </InputLabel>
              <Select
                labelId="organization-contract-filter-label"
                value={contractStatusFilter ?? ''}
                onChange={handleContractStatusChange}
                label={t('organizations_page.filters.contract_status')}
              >
                <MenuItem value="">
                  {t('organizations_page.filters.all_contracts')}
                </MenuItem>
                <MenuItem value="Expired">
                  {t('organizations_page.filters.contract_expired')}
                </MenuItem>
                <MenuItem value="ExpiringSoon">
                  {t('organizations_page.filters.contract_expiring_soon')}
                </MenuItem>
                <MenuItem value="Active">
                  {t('organizations_page.filters.contract_active')}
                </MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={handleResetFilters}>
              {t('filter_drawer.reset_filters')}
            </Button>
          </div>
        </div>
      </PageToolbar>

      <PageSpinner isLoading={!organizations}>
        <OrganizationsTable />
      </PageSpinner>
      <FlowDialog
        fullWidth={true}
        isOpen={isOpen}
        handleClose={handleCloseDialog}
        title="organizations_page.create_org.dialog_title"
        snackLinkText={`${t('common.goto')} ${organizationName}`}
        snackLink={`organization/${organizationId}/dashboard`}
        message={t('organizations_page.create_org.success_snack')}
        contentHeight={'27rem'}
      >
        <CreateOrganizationStep
          stepTitleString="organizations_page.create_org.create_org_step.title"
          buttonText="common.create_and_proceed"
        />
        <AddContractInfoStep
          stepTitleString="organizations_page.create_org.add_contract_info_step.title"
          buttonText="common.create_and_proceed"
        />
        <AddUserToOrganizationStep
          stepTitleString="organizations_page.create_org.add_user_step.title"
          buttonText="common.create_and_proceed"
        />
        <AddMeasurementCardStep
          stepTitleString="organizations_page.create_org.add_measurement_card_step.title"
          buttonText="common.create"
        />
      </FlowDialog>
    </>
  );
};

export { Organizations };
